/**
 * Hook para generar tokens reCAPTCHA v3 invisibles.
 *
 * Carga el SDK de Google una sola vez por página. Expone `executeRecaptcha`
 * que devuelve una promesa con el token para la `action` dada.
 *
 * Uso típico:
 *
 *   const { executeRecaptcha } = useRecaptcha()
 *   const token = await executeRecaptcha('repair_request')
 *   fetch(url, { headers: { 'X-Recaptcha-Token': token }, ... })
 *
 * Si VITE_RECAPTCHA_V3_SITE_KEY no está configurada, `executeRecaptcha`
 * devuelve cadena vacía (modo dev). El backend lo aceptará igual si su
 * RECAPTCHA_V3_SECRET tampoco está configurado.
 */
import { useCallback, useEffect, useRef } from 'react'

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY as string | undefined

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void
      execute: (siteKey: string, opts: { action: string }) => Promise<string>
    }
  }
}

let scriptInjected = false
let scriptReady: Promise<void> | null = null

function ensureScript(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve()
  if (scriptReady) return scriptReady

  scriptReady = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve()
      return
    }
    if (scriptInjected) {
      // Otro hook ya inyectó; esperar a window.grecaptcha
      const t = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(t)
          resolve()
        }
      }, 100)
      setTimeout(() => {
        clearInterval(t)
        reject(new Error('grecaptcha load timeout'))
      }, 10_000)
      return
    }
    scriptInjected = true
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('grecaptcha script load failed'))
    document.head.appendChild(s)
  })
  return scriptReady
}

export function useRecaptcha() {
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    ensureScript().catch((e) => {
      console.warn('reCAPTCHA no disponible:', e.message)
    })
  }, [])

  const executeRecaptcha = useCallback(async (action: string): Promise<string> => {
    if (!SITE_KEY) return ''
    try {
      await ensureScript()
      if (!window.grecaptcha) return ''
      return await new Promise<string>((resolve) => {
        window.grecaptcha!.ready(() => {
          window
            .grecaptcha!.execute(SITE_KEY, { action })
            .then((token) => resolve(token))
            .catch(() => resolve(''))
        })
      })
    } catch {
      return ''
    }
  }, [])

  return { executeRecaptcha, isEnabled: !!SITE_KEY }
}
